import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { API_ERROR_TYPES } from '@hooks-api/types/api-error-type'
import { useEffect, useRef } from 'react'
import { STM_ALLOWED_ROLES } from '../constants'
import { useStmBrandStore } from './use-stm-brand-store'

/**
   * 背景驗證當前品牌的登入狀態與角色是否仍然有效。
   *
   * ## 登入判斷邏輯  
   * StmRouters 以 store 中的 `brands.length > 0` 作為進入 AuthRoutes 的條件，
   * 這只代表「使用者曾經登入過」，不保證 token 仍然有效。
   *
   * 透過 member API 監聽角色和登入權限的驗證：
   * - 該 API 需要有效的 access-token，若 token 已過期或被作廢（如在其他分頁重新登入同品牌），
   *   後端會回傳 403（AUTHENTICATION_ERROR），代表該品牌的登入已失效。
   * - 即使 API 成功，也需檢查 roles 是否包含允許的角色（partner / shop_manager），
   *   確保使用者仍具有該品牌的存取權限。
   *
   * ## 觸發時機
   * - 首次掛載時：SWR revalidateOnMount
   * - 品牌切換時：透過 mutate() 強制 revalidate
   */
export const useStmRoleGuard = (): void => {
  const { currentBrand, setExpiredCampaignCodes } = useStmBrandStore()
  const prevBrandCodeRef = useRef(currentBrand?.campaignCode)

  const { destructuredData: campaignMember, error, mutate } = useApiCampaignMember(
    currentBrand ? { code: currentBrand.campaignCode } : undefined,
    {
      shouldRetryOnError: (err) => {
        if (err?.response?.data.error?.type === API_ERROR_TYPES.AUTHENTICATION_ERROR) {
          return false
        }
        return true
      },
    },
  )

  /**
   * 品牌切換時強制 revalidate CampaignMember
   */
  useEffect(function revalidateOnBrandSwitch() {
    if (currentBrand?.campaignCode && currentBrand.campaignCode !== prevBrandCodeRef.current) {
      prevBrandCodeRef.current = currentBrand.campaignCode
      mutate()
    }
  }, [currentBrand?.campaignCode, mutate])

  /**
   * API error（如 403 AUTHENTICATION_ERROR）或角色不符時，通知該品牌已過期。
   */
  useEffect(function markExpiredOnRoleMismatch() {
    if (!campaignMember && !error) return
    if (!currentBrand) return

    const hasRole = campaignMember?.roles.some(role => (STM_ALLOWED_ROLES as readonly string[]).includes(role))

    if (error || !hasRole) {
      setExpiredCampaignCodes(currentBrand.campaignCode)
    }
  }, [campaignMember, error, currentBrand, setExpiredCampaignCodes])
}
